import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import fm from 'format-message';
import useCheckoutUrlQuery from 'queries/useCheckoutUrlQuery';
import Events from '@activebrands/core-web/libs/Events';
import { CheckoutEvents } from '@activebrands/core-web/libs/Events/types';
import { CheckoutTracking } from '@activebrands/core-web/libs/Tracking/constants';
import { getBasketPaymentMethod } from '@activebrands/core-web/state/basket/actions';
import { addLocalePrefix } from '@activebrands/core-web/utils/add-locale-prefix';
import transformLocale from '@activebrands/core-web/utils/transform-locale';
import { isValidBasketAddress } from '@activebrands/core-web/utils/validation';
import { useLocation } from '@reach/router';

/*  To override the adyen configuration, create this object: window.adyenCheckoutConfig.
    The default values looks like this:
    {
        autoFocusOnLoad: true,
        consolidateCards: true,
        context: 'test',
        initialPMCount: 5,
        paymentMethods: {
            card: {
                separateDateInputs: false,
                placeholders: {
                    encryptedSecurityCode: '1111',
                    encryptedExpiryDate: '02/12'
                }
            }
        }
    };

    To decide where the widget will be placed,
    just render a <div id="adyen-checkout-container" /> somewhere and it will be placed there.
*/
const AdyenDropInContainer = ({ token }) => {
    const dispatch = useDispatch();

    const basket = useSelector(state => state.basket);
    const { address, paymentMethodId, id: basketId, shippingAddress } = basket;

    const isLoading = useRef(false);
    const { locale, aliasId } = useSelector(state => state.application);
    const { checkoutResponsePage, checkoutPage } = useCheckoutUrlQuery();

    const location = useLocation();

    const responseUrl = new URL(
        `${process.env.REACT_APP_GREBCOMMERCE_FRONTEND_API_URL}/basket/${basketId}/payment-callback-with-redirect/${aliasId}`
    );

    // Change the default klarna naming
    window.adyenDropInConfig = {
        // Spread potential existing configurations, so klarna config won't overwrite
        ...window.adyenDropInConfig,
        klarna: {
            name: `Klarna – ${fm('Pay later')}`,
        },
        'klarna_paynow': {
            name: `Klarna – ${fm('Pay now')}`,
        },
        'klarna_account': {
            name: `Klarna – ${fm('Partial payment')}`,
        },
    };

    // Adding url override for localhost
    if (location?.hostname === 'localhost') {
        responseUrl.searchParams.append('overrideCheckout', location.origin + addLocalePrefix(checkoutPage));
        responseUrl.searchParams.append('overrideThanks', location.origin + addLocalePrefix(checkoutResponsePage));
    }

    const getRawSnippet = script => script.text || script.textContent || script.innerHTML || '';

    const evaluate = subtree => {
        const getSnippetType = script => script.type || '';

        if (subtree) {
            try {
                const scripts = subtree.getElementsByTagName('script');
                const scriptsAsArray = Array.from(scripts).filter(
                    script => getSnippetType(script) !== 'application/json'
                );
                scriptsAsArray.forEach(script => {
                    const unescaped = getRawSnippet(script).replace(/\\/gi, '');
                    eval.call(window, unescaped);
                });
            } catch (error) {
                console.error('Catched error in evaluate function:', error);
            }
        }
    };

    const handleResponse = response => {
        const paymentCallback = response.data?.data;
        if (paymentCallback) {
            const adyenContainer = document.getElementById('adyen-checkout-container');

            if (paymentCallback.action === 'form' && adyenContainer) {
                adyenContainer.innerHTML = '';
                adyenContainer.innerHTML = paymentCallback.formHtml;
                evaluate(adyenContainer);
            }

            if (paymentCallback.action === 'redirect') {
                window.location.replace(paymentCallback?.url);
            }

            // Using code because formFields causing problems with certain card payments..
            if (paymentCallback.action === 'javascript') {
                if (paymentCallback.code) {
                    eval(paymentCallback.code);
                } else {
                    const updateEvent = new CustomEvent('centra_checkout_payment_response', {
                        detail: paymentCallback.formFields,
                    });
                    document.dispatchEvent(updateEvent);
                }
            }

            Events.trigger(CheckoutEvents.OPTION, {
                step: CheckoutTracking.PAYMENT_STEP,
                // option: shippingMethodId,
                // currencyCode: currency,
            });

            if (paymentCallback.action === 'success') {
                const updateEvent = new CustomEvent('centra_checkout_payment_response', {
                    detail: {
                        action: null,
                        resultCode: paymentCallback.order.resultCode ?? null,
                    },
                });
                document.dispatchEvent(updateEvent);
            } else {
                if (response.data?.warning && response.data?.warning?.code === 3) {
                    console.error('Item has changed');
                    // if an item has changed.
                    // This will be handled in the redux from GET_PAYMENT_METHOD_WARNING
                }
            }
        }
    };

    const centraCheckoutPaymentCallback = useCallback(
        origdata => {
            if (origdata.detail) {
                const data = { ...origdata.detail };

                // Ugly hacks because someone have name their shit wrong...
                data.address = {
                    ...data.billingAddress,
                    'zip': data.billingAddress.zipCode,
                    'phone': data.billingAddress.phoneNumber,
                };
                delete data.billingAddress;

                // Rewrite zip and phone so they match address', just to keep it the same for ... reasons.
                data.shippingAddress = {
                    ...data.shippingAddress,
                    'zip': data.shippingAddress.zipCode,
                    'phone': data.shippingAddress.phoneNumber,
                };

                data.paymentMethodId = data.paymentMethod;
                delete data.paymentMethod;

                try {
                    Events.trigger(CheckoutEvents.PAYMENT_INFO_ADDED, {
                        paymentInfo: {
                            currency: basket.currency,
                            paymentType: data.paymentMethodId,
                        },
                        items: basket.items,
                    });
                } catch (e) {
                    console.warn('Unable to trigger payment event.');
                }

                return dispatch(
                    getBasketPaymentMethod({
                        ...data,
                        locale: transformLocale(locale),
                        responseUrl: responseUrl.href,
                        token,
                    })
                ).then(handleResponse);
            }
        },
        [responseUrl]
    );

    useEffect(() => {
        if (
            !isLoading.current &&
            paymentMethodId &&
            isValidBasketAddress(address) &&
            isValidBasketAddress(shippingAddress)
        ) {
            isLoading.current = true;
            dispatch(
                getBasketPaymentMethod({
                    paymentMethodId,
                    address,
                    shippingAddress,
                    locale: transformLocale(locale),
                    responseUrl: responseUrl.href,
                    token,
                })
            ).then(response => {
                isLoading.current = false;
                // Remove eventlistener and add to make sure that we don't create duplicates
                document.removeEventListener('centra_checkout_payment_callback', centraCheckoutPaymentCallback);
                document.addEventListener('centra_checkout_payment_callback', centraCheckoutPaymentCallback);
                handleResponse(response);
            });
        }
        return () => {
            document.removeEventListener('centra_checkout_payment_callback', centraCheckoutPaymentCallback);
        };
    }, [paymentMethodId, address, shippingAddress, token]);

    return null;
};

export default AdyenDropInContainer;
